import '@patsnap-ui/icon/assets/solid/chain.svg'
import { EMPTY_PLACEHOLDER } from '@patsnap/synapse_common_config'
import { IBaseDictItem, IBaseDrugAdc, IBaseSequence, IBaseTargetInfo, IDrugDetail } from '@patsnap/synapse_common_interface'
import { getDicItemName, getLangDegraded } from '@patsnap/synapse_common_utils'
import { useLocale } from '@pharmsnap/shared/src/composition'
import { COMMA, renderPopoverLimited } from '@pharmsnap/shared/src/render'
import { computed, defineComponent, PropType, toRefs } from '@vue/composition-api'
import { isUndefined, map } from 'lodash'
import { Icon as PtIcon } from 'patsnap-biz'
import '../../../assets/icon-svg/viewStructure.svg'
import { sharedCtx } from '../../../context'
import { getDrugName, getRedirectLinkStatic } from '../../../utils'
import { GDialogTrigger } from '../../ui/GDialogTrigger/GDialogTrigger'
import { GIcon } from '../../ui/GIcon/GIcon'
import { GLink } from '../../ui/GLink/GLink'
import { BCasStructureDialog } from '../BCasStructure'
import { BSequenceDialog } from '../BSequence'

function drugAdcDictFieldComBuilder(field: keyof IBaseDrugAdc, name: string) {
  return defineComponent({
    name: name,
    props: {
      data: {
        type: Object as PropType<IBaseDrugAdc>,
        required: true,
      },
    },
    setup(props) {
      return () => {
        if (!props.data[field]) return <span>{EMPTY_PLACEHOLDER}</span>

        return <span>{getDicItemName(props.data[field] as IBaseDictItem, 'EN')}</span>
      }
    },
  })
}

export const BDrugAdcAntibodyField = defineComponent({
  name: 'BDrugAdcAntibodyField',
  props: {
    data: {
      type: Object as PropType<IBaseDrugAdc>,
      required: true,
    },
  },
  setup(props) {
    const { locale } = useLocale()
    return () => {
      if (!props.data.antibody_drug_id_view) return <span>{EMPTY_PLACEHOLDER}</span>

      const drugName = getDrugName(props.data.antibody_drug_id_view, locale.value)
      const href = sharedCtx.router.generatorDrugPath(props.data.antibody_drug_id_view.drug_id)

      return (
        <a style="word-break: break-word;" href={href} target="_blank" title={drugName} class="text-blue-default hover:underline">
          <span class="text-blue-default">{drugName}</span>
        </a>
      )
    }
  },
})

export const BDrugAdcAntibodySequenceTrigger = defineComponent({
  name: 'BDrugAdcAntibodySequenceTrigger',
  props: {
    sequence: {
      type: Array as PropType<IBaseSequence[]>,
      default: () => [],
    },
    triggerText: {
      type: String,
      required: true,
    },
  },
  setup(props, ctx) {
    function onSequenceSimilar(sequence_hash_id: string) {
      ctx.emit('clickSimilar', sequence_hash_id)
    }

    return () => {
      const sequence = props.sequence

      if (!sequence.length) return <span>{EMPTY_PLACEHOLDER}</span>

      return (
        <GDialogTrigger dialog={BSequenceDialog} onClickSimilar={onSequenceSimilar} targetSequenceList={map(sequence, (sequence) => ({ sequence }))}>
          <span class="text-sm leading-6 hover:underline cursor-pointer text-blue-default">{props.triggerText}</span>
        </GDialogTrigger>
      )
    }
  },
})

export const BDrugAdcLinkerField = drugAdcDictFieldComBuilder('linker_id_view', 'BDrugAdcLinkerField')

export const BDrugAdcLinkerTypeField = drugAdcDictFieldComBuilder('link_type_id_view', 'BDrugAdcLinkerTypeField')

export const BDrugAdcStructureTrigger = defineComponent({
  name: 'BDrugAdcLinkerStructureTrigger',
  props: {
    data: {
      type: Object as PropType<IDrugDetail & IBaseDrugAdc>,
      required: true,
    },
    field: {
      type: String as PropType<'linker_id_view' | 'payload_id_view'>,
      required: true,
    },
    triggerMode: {
      type: String as PropType<'text' | 'icon'>,
      default: 'text',
    },
    triggerText: {
      type: String,
      default: 'View Structure',
    },
  },
  setup(props, ctx) {
    const { data, field } = toRefs(props)

    const structureList = computed(() => {
      const structure =
        field.value === 'linker_id_view'
          ? data.value.structure?.filter((i) => i.structure_type === 'LINKER')
          : data.value.structure?.filter((i) => i.structure_type === 'PAYLOAD')
      if (!structure) return []
      return structure.map((i) => ({ ...i }))
    })

    function onStructureSimilar(sequence_hash_id: string) {
      ctx.emit('clickSimilar', sequence_hash_id)
    }

    return () => {
      if (!props.data[props.field]) return <span>{EMPTY_PLACEHOLDER}</span>

      return (
        <GDialogTrigger
          dialog={BCasStructureDialog}
          onClickSimilar={onStructureSimilar}
          structureList={structureList.value}
          onCasStructureItemClicked={(index: number) => ctx.emit('casStructureItemClicked', index)}
        >
          {props.triggerMode === 'text' ? (
            <span class="text-sm leading-6 hover:underline cursor-pointer text-blue-default">{props.triggerText}</span>
          ) : (
            <PtIcon class="w-6 h-6 text-blue-default cursor-pointer" icon="ViewStructure"></PtIcon>
          )}
        </GDialogTrigger>
      )
    }
  },
})

export const BDrugAdcPayloadField = drugAdcDictFieldComBuilder('payload_id_view', 'BDrugAdcPayloadField')

export const BDrugAdcDarField = defineComponent({
  name: 'BDrugAdcDarField',
  props: {
    data: {
      type: Object as PropType<IBaseDrugAdc>,
      required: true,
    },
  },
  setup(props) {
    return () => {
      if (isUndefined(props.data.dar)) return <span>{EMPTY_PLACEHOLDER}</span>
      let text = ''
      if (props.data.dar.value_type === 'RANGE') {
        const values = [props.data.dar.lower_value, props.data.dar.upper_value].filter((i) => !isUndefined(i))
        text = values.length > 1 ? values.join('-') : `${isUndefined(values[0]) ? '' : values[0]}`
      } else {
        text = `${isUndefined(props.data.dar.avg_value) ? '' : props.data.dar.avg_value}`
      }
      if (!text) return <span>{EMPTY_PLACEHOLDER}</span>
      return <span>{text}</span>
    }
  },
})

export const BDrugAdcAntibodyTypeField = drugAdcDictFieldComBuilder('antibody_type_view', 'BDrugAdcAntibodyTypeField')

export const BDrugAdcConjugationSiteField = drugAdcDictFieldComBuilder('site_id_view', 'BDrugAdcConjugationSiteField')

export const BDrugAdcSiteSpecialField = defineComponent({
  name: 'BDrugAdcSiteSpecialField',
  props: {
    data: {
      type: Object as PropType<IBaseDrugAdc>,
      required: true,
    },
  },
  setup(props) {
    return () => {
      if (isUndefined(props.data.specific_site)) return <span>{EMPTY_PLACEHOLDER}</span>

      return <span>{props.data.specific_site === 1 ? 'Yes' : 'No'}</span>
    }
  },
})

export const BDrugAdcSourceField = defineComponent({
  name: 'BDrugAdcSourceField',
  props: {
    data: {
      type: Object as PropType<IBaseDrugAdc>,
      required: true,
    },
    triggerText: {
      type: String,
      default: 'View',
    },
    triggerMode: {
      type: String as PropType<'text' | 'icon'>,
      default: 'text',
    },
  },
  setup(props) {
    return () => {
      const { locale } = useLocale()
      if (!props.data.adc_reference?.length) return <span>{EMPTY_PLACEHOLDER}</span>

      if (props.data.adc_reference.every((i) => !i.source_url)) return <span>{EMPTY_PLACEHOLDER}</span>

      const foundSource = props.data.adc_reference.find((i) => !!i.source_url)

      if (!foundSource || !foundSource.source_url) return <span>{EMPTY_PLACEHOLDER}</span>

      return (
        <GLink href={getRedirectLinkStatic(foundSource.source_url, locale.value)} defaultColor="blue" newTab>
          {props.triggerMode === 'text' ? props.triggerText : <GIcon size={24} svgName="SolidChain" color="#1667B8"></GIcon>}
        </GLink>
      )
    }
  },
})

export const BDrugAdcTargetingMoietyTypeField = defineComponent({
  name: 'BDrugAdcTargetingMoietyTypeField',
  props: {
    data: {
      type: Object as PropType<IBaseDrugAdc>,
      required: true,
    },
    limitCount: {
      type: Number,
      default: 3,
    },
    appendToBody: {
      type: Boolean,
    },
  },
  setup(props) {
    const { locale } = useLocale()

    return () => {
      const data = props.data.target_moiety_type_view
      if (!data?.length) return <span>{EMPTY_PLACEHOLDER}</span>

      return (
        <div class="inline-flex flex-wrap">
          {renderPopoverLimited(data, {
            appendToBody: props.appendToBody,
            limitCount: props.limitCount,
            truncate: false,
            render(item, index) {
              const drugTypeName = getDicItemName(item, locale.value)
              const href = sharedCtx.router.generatorDrugTypePath(item.dict_id)

              return (
                <span class="text-sm leading-6">
                  <a style="word-break: break-word;" href={href} target="_blank" title={drugTypeName} class="text-blue-default hover:underline">
                    <span class="text-blue-default whitespace-normal">{drugTypeName}</span>
                  </a>
                  {index !== data.length - 1 ? COMMA : ''}
                </span>
              )
            },
          })}
        </div>
      )
    }
  },
})

export const BDrugAdcTargetField = defineComponent({
  name: 'BDrugAdcTargetField',
  props: {
    target: {
      type: Array as PropType<IBaseTargetInfo[]>,
      required: true,
    },
    limitCount: {
      type: Number,
      default: 3,
    },
    appendToBody: {
      type: Boolean,
    },
  },
  setup(props) {
    return () => {
      if (!props.target?.length) return <span>{EMPTY_PLACEHOLDER}</span>

      const { locale } = useLocale()

      return (
        <div class="max-w-full">
          {renderPopoverLimited(props.target, {
            appendToBody: props.appendToBody,
            limitCount: props.limitCount,
            truncate: false,
            render(item, index) {
              const targetName = getLangDegraded(item, locale.value)
              const href = sharedCtx.router.generatorTargetPath(item.target_id)

              return (
                <div class="text-sm leading-6 truncate">
                  <GLink
                    href={href}
                    name={targetName}
                    encryptClass={item.fontClassName || ''}
                    style="word-break: break-word;"
                    class="inline text-blue-default hover:underline"
                  ></GLink>
                  {index !== props.target.length - 1 ? COMMA : ''}
                </div>
              )
            },
          })}
        </div>
      )
    }
  },
})
